import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import classNames from 'classnames';
import { getSnapshot } from 'mobx-state-tree';

import { ActionButton } from 'core/components/action-button';
import { useStore } from 'core/store';
import { OfferStatus } from 'core/store/offer-store';
import useErrors from 'common/hooks/use-errors';
import x from 'core/assets/svg/times.svg';
import xWhite from 'core/assets/svg/close.svg';

import {
  brandedTitleCarsWarning,
  powerOutage,
  purchaseFailed,
  branchSpinner,
  somethingWrong,
  vinDiscrepancy,
  mvrRetry,
  mvrRejected,
  duplicatedBind
} from './resolvers';
import useStyles from './dialog.styles';

const getOnlyErrors = (code) => code < 10000;
const getOnlyRejections = (code) => code >= 10000;

const OfferDialog = observer(({ onClose, fromCheckout }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const errors = useErrors(getSnapshot(store.errors), getOnlyErrors);
  const rejections = useErrors(getSnapshot(store.errors), getOnlyRejections);
  const isRed = errors[0] && Number(errors[0].code) < 4002 && Number(errors[0].code) !== 0;
  const vinDiscrepancies = store.vinDiscrepancies;

  // don't let the user close the dialog if we're in the middle of a purchase
  const hideCloseButton = fromCheckout && store.status === OfferStatus.Purchasing;

  const ModalContent = useMemo(() => {
    if (fromCheckout && store.status === OfferStatus.Purchasing) {
      return branchSpinner;
    }
    if (
      fromCheckout &&
      (store.status === OfferStatus.PurchaseFailed || store.status === OfferStatus.PreBindRejectionsBlocking)
    ) {
      return purchaseFailed;
    }
    if (vinDiscrepancies && vinDiscrepancies.length) {
      return vinDiscrepancy;
    }
    if (fromCheckout && store.status === OfferStatus.MVRRetry) {
      return mvrRetry;
    }
    if (store.hasNewBrandedTitleCars) {
      return brandedTitleCarsWarning;
    }
    if ((fromCheckout && store.status === OfferStatus.MVRRejected) || store.status === OfferStatus.MVRDriversChanged) {
      return mvrRejected;
    }

    if (fromCheckout && store.status === OfferStatus.DuplicatedBindError) {
      return duplicatedBind;
    }

    if (isRed) {
      return powerOutage;
    }
    if (!isRed) {
      return somethingWrong;
    }
  }, [isRed, fromCheckout, store.status, vinDiscrepancies, store.hasNewBrandedTitleCars]);

  if (!ModalContent) {
    return null;
  }

  return (
    <Dialog maxWidth="lg" open={!!store.openDialog || isRed || false}>
      <DialogContent className={classNames(classes.mContent, { [classes.redBack]: isRed })}>
        {ModalContent && (
          <ModalContent classes={classes} errors={errors} rejections={rejections} vinDiscrepancies={vinDiscrepancies} />
        )}
        {!hideCloseButton && (
          <ActionButton
            size="big"
            type="edit"
            icon={isRed ? xWhite : x}
            className={classes.closeButton}
            onClick={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
});

OfferDialog.defaultProps = {
  fromCheckout: false
};

OfferDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  fromCheckout: PropTypes.bool
};

export default OfferDialog;
